import {
  decomposeFamilyNameToKeys,
  getFamilyNamePart,
} from './familyNameMatching';
import {
  MAX_LEARNED_FAMILY_NAME_LENGTH,
  MAX_LEARNED_LIBRARY_ENTRIES,
  MAX_LEARNED_PATTERN_KEY_LENGTH,
  MAX_LEARNED_PATTERN_KEYS,
  MAX_LEARNED_VALUE_LENGTH,
  type LearnedAutoFillMatch,
  type LearnedAutoFillPatternV1,
  type LearnedAutoFillValuesV1,
} from './learnedAutoFillContract';

type QuickFillLearningRow = Record<string, unknown>;

function getText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function getOptionalValue(value: unknown): string | undefined {
  const text = getText(value);
  return text || undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isBoundedText(value: unknown, maxLength: number, required = true): value is string {
  return typeof value === 'string' &&
    value.length <= maxLength &&
    (!required || value.trim().length > 0);
}

function isTimestamp(value: unknown): value is string {
  return isBoundedText(value, 64) &&
    value.includes('T') &&
    Number.isFinite(Date.parse(value));
}

export function createLearnedPatternSignature(requiredKeys: readonly string[]): string {
  return Array.from(new Set(requiredKeys.map((key) => key.trim().toLocaleLowerCase()).filter(Boolean)))
    .sort((left, right) => left.localeCompare(right))
    .join('|');
}

export function getLearnedRequiredKeysValidationError(
  requiredKeys: readonly string[]
): string | undefined {
  if (requiredKeys.length === 0) {
    return 'Enter at least one required key.';
  }
  if (requiredKeys.length > MAX_LEARNED_PATTERN_KEYS) {
    return `Use at most ${MAX_LEARNED_PATTERN_KEYS} required keys.`;
  }
  if (requiredKeys.some((key) => key.length > MAX_LEARNED_PATTERN_KEY_LENGTH)) {
    return `Each required key must be ${MAX_LEARNED_PATTERN_KEY_LENGTH} characters or fewer.`;
  }
  return undefined;
}

export function hasLearnedAutoFillMappingValue(
  values: LearnedAutoFillValuesV1
): boolean {
  return Object.values(values).some(
    (value) => typeof value === 'string' && value.trim().length > 0
  );
}

export function buildLearnedAutoFillPattern(
  item: QuickFillLearningRow,
  updatedAt = new Date().toISOString()
): LearnedAutoFillPatternV1 | null {
  const sourceFamilyName = getFamilyNamePart(
    getText(item.__familyName) || getText(item.Name)
  );
  const objectType = getText(item['OBJECT-TYPE']);
  const skey = getText(item.SKEY);
  const requiredKeys = decomposeFamilyNameToKeys(sourceFamilyName);
  const values: LearnedAutoFillValuesV1 = {
    objectType,
    skey,
    ...(getOptionalValue(item.End1) ? { end1: getOptionalValue(item.End1) } : {}),
    ...(getOptionalValue(item.End2) ? { end2: getOptionalValue(item.End2) } : {}),
    ...(getOptionalValue(item.End3) ? { end3: getOptionalValue(item.End3) } : {}),
    ...(getOptionalValue(item.End4) ? { end4: getOptionalValue(item.End4) } : {}),
  };

  if (!sourceFamilyName || requiredKeys.length === 0 || !hasLearnedAutoFillMappingValue(values)) {
    return null;
  }

  return sanitizeLearnedAutoFillPattern({
    id: createLearnedPatternSignature(requiredKeys),
    sourceFamilyName,
    requiredKeys,
    values,
    updatedAt,
  });
}

export function matchLearnedAutoFillPattern(
  familyName: string,
  patterns: readonly LearnedAutoFillPatternV1[]
): LearnedAutoFillMatch | null {
  const candidates = rankLearnedAutoFillPatterns(familyName, patterns);
  const selected = candidates[0];

  if (!selected) {
    return null;
  }

  return {
    ...selected.values,
    confidence: 100,
    matchedPatternId: `learned:${selected.id}`,
    matchedKeywords: [...selected.requiredKeys],
  };
}

export function rankLearnedAutoFillPatterns(
  familyName: string,
  patterns: readonly LearnedAutoFillPatternV1[]
): LearnedAutoFillPatternV1[] {
  const familyKeys = new Set(decomposeFamilyNameToKeys(familyName));
  return patterns
    .filter((pattern) => pattern.requiredKeys.every((key) => familyKeys.has(key)))
    .sort((left, right) =>
      right.requiredKeys.length - left.requiredKeys.length ||
      right.updatedAt.localeCompare(left.updatedAt) ||
      left.id.localeCompare(right.id)
    );
}

function sanitizeRequiredKeys(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.some((key) => typeof key !== 'string')) {
    return null;
  }

  const keys = value.flatMap((key) =>
    key.length <= MAX_LEARNED_PATTERN_KEY_LENGTH
      ? decomposeFamilyNameToKeys(key)
      : []
  );
  const uniqueKeys = Array.from(new Set(keys));
  return getLearnedRequiredKeysValidationError(uniqueKeys) ? null : uniqueKeys;
}

function sanitizeValues(value: unknown): LearnedAutoFillValuesV1 | null {
  if (!isRecord(value)) {
    return null;
  }

  if (
    !isBoundedText(value.objectType, MAX_LEARNED_VALUE_LENGTH, false) ||
    !isBoundedText(value.skey, MAX_LEARNED_VALUE_LENGTH, false)
  ) {
    return null;
  }

  const optionalFields = ['end1', 'end2', 'end3', 'end4'] as const;
  if (optionalFields.some((field) =>
    value[field] !== undefined && !isBoundedText(value[field], MAX_LEARNED_VALUE_LENGTH, false)
  )) {
    return null;
  }

  const values: LearnedAutoFillValuesV1 = {
    objectType: value.objectType.trim(),
    skey: value.skey.trim(),
    ...Object.fromEntries(
      optionalFields
        .map((field) => [field, getOptionalValue(value[field])] as const)
        .filter((entry): entry is readonly [typeof optionalFields[number], string] => Boolean(entry[1]))
    ),
  };

  return hasLearnedAutoFillMappingValue(values) ? values : null;
}

export function sanitizeLearnedAutoFillPattern(value: unknown): LearnedAutoFillPatternV1 | null {
  if (!isRecord(value)) {
    return null;
  }

  const sourceFamilyName = isBoundedText(value.sourceFamilyName, MAX_LEARNED_FAMILY_NAME_LENGTH)
    ? getFamilyNamePart(value.sourceFamilyName)
    : '';
  const requiredKeys = sanitizeRequiredKeys(value.requiredKeys);
  const values = sanitizeValues(value.values);
  const updatedAt = isTimestamp(value.updatedAt) ? value.updatedAt : '';

  if (!sourceFamilyName || !requiredKeys || !values || !updatedAt) {
    return null;
  }

  return {
    id: createLearnedPatternSignature(requiredKeys),
    sourceFamilyName,
    requiredKeys,
    values,
    updatedAt,
  };
}

export function sanitizeLearnedAutoFillPatterns(value: unknown): LearnedAutoFillPatternV1[] {
  if (!Array.isArray(value) || value.length > MAX_LEARNED_LIBRARY_ENTRIES) {
    return [];
  }

  const entries = value.map(sanitizeLearnedAutoFillPattern).filter(
    (entry): entry is LearnedAutoFillPatternV1 => entry !== null
  );
  const unique = new Map(entries.map((entry) => [entry.id, entry]));
  return Array.from(unique.values()).sort((left, right) => left.id.localeCompare(right.id));
}
